import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";

// Één visuele programma-architectuur: START → VERDER → Volgende beweging.
// Geen Basis/Compleet. Geen jaarprogramma. Eén verhaal.

const STAPPEN = [
  {
    k: "start",
    tint: "#E06B3F",
    label: "START",
    duur: "12 weken",
    korte: "Ontdekken wat remt. Vinden wat werkt. Een eerste beweging maken.",
    voorGemeente: "Basisinvestering. Alle deelnemers doen START.",
    to: "/programma?programma=start",
  },
  {
    k: "beslismoment",
    tint: "#D4A94A",
    label: "Beslismoment",
    duur: "week 12",
    korte: "Samen bekijken: past een verdieping, of is de volgende stap al zichtbaar?",
    voorGemeente: "Beslismoment met deelnemer, gemeente en LevelLeven. Geen extra kosten.",
    to: "/methode",
  },
  {
    k: "verder",
    tint: "#78A85A",
    label: "VERDER",
    duur: "mogelijk vervolg",
    korte: "Geen automatische voortzetting. Alleen als het past, in overleg. Invulling wordt nog uitgewerkt.",
    voorGemeente: "Alleen voor wie het nodig heeft. Optioneel, in overleg.",
    to: "/programma?programma=verder",
  },
  {
    k: "volgende",
    tint: "#97B79D",
    label: "Volgende beweging",
    duur: "buiten",
    korte: "Participatie · opleiding · vrijwilligerswerk · bestaand traject · werk.",
    voorGemeente: "Warme overdracht naar bestaande voorzieningen. Waar afgesproken een vervolgmeting.",
    to: "/voor-gemeenten#onderzoek",
  },
];

export default function ProgrammaArchitectuur({ perspective = "opdrachtgever" }) {
  return (
    <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="programma-architectuur">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Zo bouwt LevelLeven op</p>
          <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
            Eén verhaal.{" "}
            <span className="italic text-ll-coral">Vier stappen.</span>
          </h2>
          <p className="mt-6 text-ll-navy/80 leading-relaxed">
            {perspective === "deelnemer"
              ? "Iedereen begint met START. Daarna kijken we samen wat past. Soms is dat meer tijd, soms een stap naar buiten. Meer hoeft niet."
              : "LevelLeven START (12 weken) is een afgerond programma. Na het beslismoment kiest u samen met de deelnemer de volgende stap: meestal een bestaande voorziening, soms een mogelijk vervolg (VERDER)."}
          </p>
        </div>

        <div className="hidden md:flex flex-wrap items-stretch gap-4">
          {STAPPEN.map((s, i) => (
            <div key={s.k} className="flex items-stretch gap-3">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ delay: i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-3xl p-6 flex flex-col justify-between min-h-[240px] w-[220px]"
                style={{ background: s.tint, color: s.k === "volgende" || s.k === "beslismoment" ? "#1F5132" : "#FAF7F1" }}
              >
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">{s.duur}</p>
                  <p className="mt-2 font-heading text-2xl font-light">{s.label}</p>
                </div>
                <p className="mt-6 text-sm leading-relaxed">{s.korte}</p>
                <Link to={s.to} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] hover:opacity-80" data-testid={`arch-${s.k}-link`}>
                  Meer <ArrowUpRight size={12} />
                </Link>
              </motion.div>
              {i < STAPPEN.length - 1 && (
                <div className="flex items-center text-ll-ink/40">
                  <ArrowRight size={22} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* MOBILE STACK */}
        <div className="md:hidden space-y-3">
          {STAPPEN.map((s) => (
            <div
              key={s.k}
              className="rounded-2xl p-6"
              style={{ background: s.tint, color: s.k === "volgende" || s.k === "beslismoment" ? "#1F5132" : "#FAF7F1" }}
            >
              <p className="text-[10px] uppercase tracking-[0.3em] opacity-80">{s.duur}</p>
              <p className="mt-2 font-heading text-2xl font-light">{s.label}</p>
              <p className="mt-3 text-sm leading-relaxed">{s.korte}</p>
            </div>
          ))}
        </div>

        {perspective === "opdrachtgever" && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-ll-navy/70">
            {STAPPEN.map((s) => (
              <p key={s.k} className="p-3 rounded-lg bg-white border border-ll-ink/10">
                <span className="text-[10px] uppercase tracking-[0.28em]" style={{ color: s.tint }}>{s.label}</span>
                <br />
                {s.voorGemeente}
              </p>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
